import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PublicLayout } from '@/components/public';
import { TalkToSalesButton } from '@/components/owner/TalkToSales';
import { Check, Gift, ArrowRight, HelpCircle, Building, Users, Crown, Star } from 'lucide-react';

import { t } from '@/i18n';
import { NocPackagesPromo } from '@/components/public/NocPackagesPromo';
const PricingPage = () => {
  const navigate = useNavigate();

  // Tiered pricing structure
  const tiers = [
    {
      name: t("Basic"),
      price: 45,
      description: t("1–24 notices per month"),
      features: [
        t("All preliminary notice types"),
        t("State-compliant documents"),
        t("Certified mail delivery"),
        t("Online dashboard access"),
        t("Email notifications"),
        t("Free lien waivers"),
      ],
      icon: Users,
      popular: false,
      cta: t("Get Started"),
    },
    {
      name: 'Pro',
      price: 35,
      description: t("25–99 notices per month"),
      features: [
        t("Everything in Basic"),
        t("Priority document processing"),
        t("Dedicated account manager"),
        t("Bulk order discounts"),
        t("Advanced reporting"),
        t("API access"),
      ],
      icon: Star,
      popular: true,
      cta: t("Start Pro"),
    },
    {
      name: t("Enterprise"),
      price: null,
      description: t("100+ notices per month"),
      features: [
        t("Everything in Pro"),
        t("Custom pricing agreement"),
        t("White-label options"),
        t("Custom integrations"),
        t("Dedicated support line"),
        t("SLA guarantees"),
      ],
      icon: Building,
      popular: false,
      cta: t("Contact Sales"),
    },
  ];

  const fixedPricingDocs = [
    { name: t("Florida Notice of Commencement — full package"), price: 135, desc: t("Property research, owner coordination, online notarization, e-recording and the county first-page fee. $115 each once you order 25+ in a month or upload 25+ at once. 50+ a month: talk to sales.") },
    { name: t("Notarized job-site posting statement"), price: 15, desc: t("F.S. 713.13(2). Issued the day we file, so you can post before the clerk records it. Included free with rush.") },
    { name: t("Mechanics Lien Filing"), price: 250, desc: t("Complete lien document preparation and filing") },
    { name: t("Bond Claim"), price: 125, desc: t("Payment bond claim documentation") },
    { name: t("Notice of Intent to Lien"), price: 125, desc: t("Warning notice before lien filing") },
    { name: t("Satisfaction of Lien"), price: 125, desc: t("Lien release documentation") },
    { name: t("Notarized Affidavit of Mailing"), price: 35, desc: t("Sworn, notarized proof your notices were mailed") },
  ];

  const postageRates = [
    { name: t("1st Class Mail"), price: 0.78, desc: t("Standard delivery"), popular: false },
    { name: t("Certified Mail (E-Return Receipt)"), price: 10.44, desc: t("Electronic return receipt"), popular: true },
    { name: t("Registered Mail"), price: 31.80, desc: t("Maximum security delivery"), popular: false },
  ];

  const includedFeatures = [
    t("State-compliant document generation"),
    t("Property & recipient research (full service)"),
    t("USPS certified mail with tracking"),
    t("Proof of service documentation"),
    t("Online dashboard access"),
    t("Email notifications & reminders"),
    t("Document storage & history"),
    t("Free lien waivers & releases"),
    t("Dedicated customer support"),
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-[#1e3a5f] via-[#2d4a6f] to-[#1e3a5f]" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-[#F97316] text-white mb-4">{t("Transparent Pricing")}</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
            {t("Simple, Volume-Based Pricing")}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-4">
            {t("No monthly fees. No hidden costs. The more you send, the more you save.")}
          </p>
          <p className="text-slate-400 text-sm">
            {t("Join thousands of contractors who trust All American Notice")}
          </p>
        </div>
      </section>

      {/* Pricing Banner */}
      <section className="bg-[#F97316] py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-white">
            <Gift className="h-8 w-8 flex-shrink-0" />
            <div className="text-center sm:text-left">
              <span className="font-bold text-xl">{t("Notices from $35")}</span>
              <p className="text-white/90 text-sm">{t("$35–$45 per notice + certified mail postage. No subscription.")}</p>
            </div>
            <Button 
              onClick={() => navigate('/register')} 
              variant="secondary" 
              className="bg-white text-[#F97316] hover:bg-slate-100 font-semibold"
            >
              {t("Get Started")}
            </Button>
          </div>
        </div>
      </section>

      {/* Tiered Pricing */}
      <section className="py-16 bg-white" data-testid="tiered-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              {t("Choose Your Plan")}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t("All plans include full-service document preparation and certified mail delivery.")} 
              <br />{t("Price shown is per preliminary notice + postage.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {tiers.map((tier, index) => {
              const Icon = tier.icon;
              return (
                <Card 
                  key={index}
                  className={`relative border-2 ${tier.popular ? 'border-[#F97316] shadow-xl scale-105' : 'border-slate-200'} bg-white overflow-hidden`}
                  data-testid={`tier-${tier.name.toLowerCase()}`}
                >
                  {tier.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-[#F97316] text-white text-center py-1 text-sm font-semibold">
                      <Crown className="h-4 w-4 inline mr-1" />
                      {t("Most Popular")}
                    </div>
                  )}
                  <CardHeader className={`text-center ${tier.popular ? 'pt-10' : 'pt-6'}`}>
                    <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4 ${tier.popular ? 'bg-[#F97316]/10 text-[#F97316]' : 'bg-slate-100 text-slate-600'}`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    <CardTitle className="text-2xl text-[#1e3a5f]">{tier.name}</CardTitle>
                    <CardDescription className="text-slate-500">{tier.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="mb-6">
                      {tier.price !== null ? (
                        <>
                          <span className="text-4xl font-black text-[#F97316]">${tier.price}</span>
                          <span className="text-slate-500">{t("/notice + postage")}</span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-[#1e3a5f]">{t("Custom Pricing")}</span>
                      )}
                    </div>
                    <ul className="space-y-3 text-left mb-6">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      onClick={() => navigate(tier.name === 'Enterprise' ? '/contact' : '/register')}
                      className={`w-full ${tier.popular ? 'bg-[#F97316] hover:bg-[#ea6a10]' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'} text-white`}
                    >
                      {tier.cta}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="text-center text-slate-500 text-sm mt-8">
            {t("Volume tiers are based on the notices you send in the calendar month. Pricing automatically adjusts as your volume changes.")}
          </p>
        </div>
      </section>

      {/* Fixed Price Documents */}
      <section className="py-16 bg-slate-50" data-testid="fixed-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              {t("Additional Document Services")}
            </h2>
            <p className="text-slate-600">{t("Fixed pricing for liens, bond claims, and more")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fixedPricingDocs.map((item, index) => (
              <Card key={index} className="border-2 border-slate-200 bg-white hover:border-[#F97316] transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg text-[#1e3a5f]">{item.name}</CardTitle>
                    <span className="text-2xl font-black text-[#F97316]">${item.price}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8" data-testid="pricing-noc-sales">
            <span className="text-slate-600 text-sm">{t("Owner, developer or GC filing 50+ NOCs a month?")}</span>
            <TalkToSalesButton />
            <Button variant="outline" onClick={() => navigate('/register?type=owner')} data-testid="pricing-owner-signup">{t("Open an Owner / Developer account")}</Button>
          </div>
          <div className="mt-10 text-left"><NocPackagesPromo /></div>
        </div>
      </section>

      {/* Postage Rates */}
      <section className="py-16 bg-white" data-testid="postage-rates">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">{t("Postage Rates")}</h2>
            <p className="text-slate-600">{t("Per recipient - based on current USPS rates")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {postageRates.map((item, index) => (
              <Card 
                key={index} 
                className={`border-2 ${item.popular ? 'border-[#F97316] shadow-lg scale-105' : 'border-slate-200'} bg-white`}
              >
                <CardHeader className="text-center pb-2">
                  {item.popular && <Badge className="bg-[#F97316] mb-2 mx-auto">{t("Recommended")}</Badge>}
                  <CardTitle className="text-lg text-[#1e3a5f]">{item.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="text-4xl font-black text-[#F97316] mb-2">${item.price}</div>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Formula */}
      <section className="py-16 bg-slate-50" data-testid="pricing-formula">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-[#1e3a5f] shadow-lg overflow-hidden">
            <CardHeader className="text-center bg-[#1e3a5f]">
              <CardTitle className="text-2xl text-white">{t("How Pricing Works")}</CardTitle>
              <CardDescription className="text-lg text-slate-300">{t("Simple formula, no surprises")}</CardDescription>
            </CardHeader>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-lg sm:text-2xl font-bold text-[#1e3a5f] bg-slate-100 px-6 py-4 rounded-xl">
                  <span>{t("Document Fee")}</span>
                  <span className="text-[#F97316]">+</span>
                  <span>(Postage × Recipients)</span>
                  <span className="text-[#F97316]">=</span>
                  <span className="text-[#F97316]">{t("Total")}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#1e3a5f]/5 p-6 rounded-xl border border-[#1e3a5f]/20">
                  <h3 className="font-bold text-lg mb-2 text-[#1e3a5f]">{t("Example: Basic Plan")}</h3>
                  <p className="text-slate-600 text-sm mb-4">{t("Preliminary Notice to 2 recipients")}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Document Fee (Basic):</span><span className="font-medium">$45.00</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">{t("Certified Mail × 2:")}</span><span className="font-medium">$20.88</span></div>
                    <div className="flex justify-between font-bold border-t border-slate-300 pt-2 mt-2">
                      <span>{t("Total:")}</span><span className="text-[#F97316] text-lg">$65.88</span>
                    </div>
                  </div>
                </div>
                <div className="bg-[#F97316]/5 p-6 rounded-xl border border-[#F97316]/20">
                  <h3 className="font-bold text-lg mb-2 text-[#1e3a5f]">{t("Example: Pro Plan")}</h3>
                  <p className="text-slate-600 text-sm mb-4">{t("Preliminary Notice to 5 recipients")}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-600">Document Fee (Pro):</span><span className="font-medium">$35.00</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">{t("Certified Mail × 5:")}</span><span className="font-medium">$52.20</span></div>
                    <div className="flex justify-between font-bold border-t border-slate-300 pt-2 mt-2">
                      <span>{t("Total:")}</span><span className="text-[#F97316] text-lg">$87.20</span>
                    </div>
                  </div>
                  <p className="text-xs text-green-600 mt-2 font-medium">Save $10 per notice on Pro!</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white" data-testid="whats-included">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">{t("What's Included")}</h2>
            <p className="text-slate-600">{t("Everything you need to protect your lien rights - included in all plans")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {includedFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Request Form CTA */}
      <section className="py-12 bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f]" data-testid="job-form-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">{t("Prefer to Submit Orders by Form?")}</h2>
          <p className="text-slate-200 mb-6">Download our Job Request Form, fill it out, and email or fax it to us. We&apos;ll handle the rest!</p>
          <Button 
            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/forms/job-request`, '_blank')}
            className="bg-[#F97316] hover:bg-[#ea6a10] text-white font-semibold px-8 py-3"
            data-testid="download-job-form-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Job Request Form (PDF)
          </Button>
          <p className="text-slate-300 text-sm mt-3">{t("Email to: admin@allamericannotice.com | Fax: 561-680-2857")}</p>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] mb-4">{t("Common Questions")}</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: t("Are there any monthly fees?"), a: t("No! There are no monthly fees, subscription costs, or setup charges. You only pay for the notices you send.") },
              { q: t("How do volume tiers work?"), a: t("Your pricing tier is based on your rolling 30-day notice volume. As you send more notices, you automatically qualify for lower rates.") },
              { q: t("How does pricing work?"), a: t("Pricing is simple: $35–$45 per notice plus certified mail postage. No subscription, no setup fee, no volume commitment — you only pay when you send a notice.") },
              { q: t("What payment methods do you accept?"), a: t("We accept all major credit cards through our secure Stripe payment processing.") },
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-[#F97316] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-2">{item.q}</h3>
                    <p className="text-slate-600">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button 
              variant="outline" 
              onClick={() => navigate('/faq')}
              className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
            >
              {t("View All FAQs")}
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t("Ready to Protect Your Lien Rights?")}
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            {t("Send your first notice with confidence — no subscription, no setup fee.")}
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')} 
            className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-lg px-10 h-14 font-semibold"
          >
            {t("Get Started")} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PricingPage;
